import { useCallback, useId } from "react";
import { Button } from "@/components/ui/button";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { SortableOptionRow } from "./SortableOptionRow";

/** Props for the SelectOptionsEditor component. */
export interface SelectOptionsEditorProps {
  options: string[];
  onChange: (options: string[]) => void;
  disabled: boolean;
}

/**
 * Sub-editor for defining the list of options for a select custom field.
 * Supports drag-and-drop reordering and enforces uniqueness by highlighting duplicates.
 * @param options - The current list of option values
 * @param onChange - Callback when the options list changes
 * @param disabled - Whether editing is disabled
 */
export function SelectOptionsEditor({
  options,
  onChange,
  disabled,
}: SelectOptionsEditorProps) {
  const dndId = useId();
  const nonEmptyValues = options.filter((o) => o.length > 0);
  const lowerValues = nonEmptyValues.map((v) => v.toLowerCase());
  const duplicateLowerSet = new Set(
    lowerValues.filter((v, i) => lowerValues.indexOf(v) !== i),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const optionIds = options.map((_, i) => `opt-${i}`);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (disabled) {
        return;
      }
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = optionIds.indexOf(String(active.id));
      const newIndex = optionIds.indexOf(String(over.id));
      onChange(arrayMove(options, oldIndex, newIndex));
    },
    [disabled, options, optionIds, onChange],
  );

  return (
    <div className="mx-3 mb-2.5 ml-6 rounded-md border border-dashed border-muted-foreground/25 bg-muted/20 p-2.5 space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">Options</p>
      <DndContext
        id={dndId}
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={optionIds}
          strategy={verticalListSortingStrategy}
        >
          <AnimatePresence initial={false}>
            {options.map((option, optIndex) => (
              <SortableOptionRow
                key={optionIds[optIndex]}
                id={optionIds[optIndex]}
                value={option}
                index={optIndex}
                isDuplicate={
                  option.length > 0 &&
                  duplicateLowerSet.has(option.toLowerCase())
                }
                disabled={disabled}
                onValueChange={(val) => {
                  const updated = options.map((o, i) =>
                    i === optIndex ? val : o,
                  );
                  onChange(updated);
                }}
                onRemove={() =>
                  onChange(options.filter((_, i) => i !== optIndex))
                }
              />
            ))}
          </AnimatePresence>
        </SortableContext>
      </DndContext>
      {duplicateLowerSet.size > 0 && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="text-xs text-destructive"
        >
          Duplicate options are not allowed
        </motion.p>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange([...options, ""])}
        disabled={disabled}
        className="h-7 text-xs px-2.5 text-muted-foreground hover:text-foreground"
      >
        <Plus className="mr-1 h-3 w-3" />
        Add option
      </Button>
    </div>
  );
}
